// @ts-strict-ignore
import {Injectable} from "@angular/core";
import {interval} from "rxjs";
import {take} from "rxjs/operators";
import dayjs from "dayjs";
import {
	ClientProperties,
	DisplayedMedia,
	ISimSession,
	Participant,
	ParticipantType,
	ScenarioRecording, SchedulingConflict,
	SearchParameters,
	SessionType,
	TrainingRoom,
} from "@models";
import Enumerable from "linq";
import {Translations} from "@translations";
import {TypePredicates} from "../../../shared/utilities/type-predicates";
import {
	LoadingDataParams,
} from "../../../shared/shared-components/basic-administration-table-page/administration-table-page.types";
import {KeyValue} from "@angular/common";
import {IIsReadyForArchive} from "../../../shared/shared-components/event-selection/event-selection.component";
import {isEqual} from "lodash-es";

/**
 * Provides basic utility methods
 */
@Injectable({
	providedIn: "root",
})
export class SimUtilityService {

	/**
	 * isPageReloaded
	 *
	 * determine whether page is reloaded or not
	 */
	public isPageReloaded() {
		let isPageReloaded = false;
		if (window.performance) {
			if (window.performance.navigation.type == window.performance.navigation.TYPE_RELOAD) {
				isPageReloaded = true;
			} else {
				isPageReloaded = false;
			}
		}
		return isPageReloaded;
	}

	public isWindowsBlocked(window: Window) {
		return !window || window.closed || typeof window.closed == "undefined";
	}

	/**
	 * startWith
	 *
	 * @param str
	 * @param pattern
	 */
	public startsWith(str: string, pattern: string): boolean {
		return str.indexOf(pattern) == 0;
	}

	public convertISO8601DateTodayjs(strDate: string, convertUtcToLocal: boolean = false) {
		// 2020-05-05, Alex
		// See: SIMSOFT-4449
		if (!convertUtcToLocal) {
			// 2019-10-10, Alex
			// See: SIMSOFT-3655
			let utcRemovedDate = this.trim(strDate, "Z");

			let timezoneRemovedDate = this.removeTimeZoneFromDateString(utcRemovedDate);

			return dayjs(timezoneRemovedDate);
		} else {
			return dayjs(strDate).local();
		}
	}

	/**
	 * Converts:
	 * 2013-02-04T14:44:30+01:00 to 2013-02-04T14:44:30
	 * 2013-02-04T14:44:30-01:00 to 2013-02-04T14:44:30
	 * @param strDate
	 */
	public removeTimeZoneFromDateString(strDate: string) {
		let regex = new RegExp(/[\+\-][0-9]{2}:[0-9]{2}$/g);

		return strDate.replace(regex, "");
	}

	public trimLeft(str: string, charlist: string) {
		if (charlist === undefined) {
			charlist = "s";
		}

		return str.replace(new RegExp("^[" + charlist + "]+"), "");
	}

	public trimRight(str: string, charlist: string) {
		if (charlist === undefined) {
			charlist = "s";
		}

		return str.replace(new RegExp("[" + charlist + "]+$"), "");
	}

	public trim(str: string, charlist: string) {
		charlist = charlist || "s";

		return this.trimRight(this.trimLeft(str, charlist), charlist);
	}

	/**
	 * endsWith
	 *
	 * @param str
	 * @param pattern
	 */
	public endsWith(str: string, pattern: string): boolean {
		return str.lastIndexOf(pattern) + pattern.length === str.length;
	}

	/**
	 * padLeft
	 *
	 * @param number
	 * @param length
	 */
	public padLeft(number: number, length: number): string {
		let str = "" + number;

		while (str.length < length) {
			str = "0" + str;
		}

		return str;
	}

	/**
	 * round
	 *
	 * @param number
	 * @param decimals
	 */
	public static round(number: number, decimals: number): number {
		return Math.round(number * Math.pow(10, decimals)) / Math.pow(10, decimals);
	}

	/**
	 * getMimeType
	 *
	 * @param file
	 */
	public getMimeType(file: string): string {
		if (this.endsWith(file, ".png")) {
			return "image/png";
		} else if (this.endsWith(file, ".jpg")) {
			return "image/jpeg";
		} else if (this.endsWith(file, ".jpeg")) {
			return "image/jpeg";
		} else {
			return "image/png";
		}
	}

	/**
	 * createGuid
	 *
	 * [StackOverflow]{@link http://stackoverflow.com/questions/105034/create-guid-uuid-in-javascript}
	 */
	public static createGuid(): string {
		let d = new Date().getTime();
		if (window.performance && typeof window.performance.now === "function") {
			d += performance.now(); //use high-precision timer if available
		}
		return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
			const r = (d + Math.random() * 16) % 16 | 0;
			d = Math.floor(d / 16);
			return (c == "x" ? r : (r & 0x3) | 0x8).toString(16);
		});
	}

	public createGuid(): string {
		return SimUtilityService.createGuid();
	}

	/**
	 * Returns empty Guid
	 *
	 * @return {string} Guid format
	 */
	public static emptyGuid() {
		return "00000000-0000-0000-0000-000000000000";
	}

	/**
	 * sanatizeFolderName
	 *
	 * @param name
	 */
	public sanatizeFolderName(name: string) {
		// Entities from:
		// http://www.freeformatter.com/html-entities.html
		const sanatizedName = name
			.replace(/\\/g, "&#92;")
			.replace(/\//g, "&#47;")
			.replace(/\:/g, "&#58;")
			.replace(/\*/g, "&#42;")
			.replace(/\?/g, "&#63;")
			.replace(/</g, "&lt;")
			.replace(/>/g, "&gt;")
			.replace(/\|/g, "&#124;");

		return sanatizedName;
	}

	/**
	 * Evaluates if empty object or not
	 *
	 * @param obj
	 * @return {boolean}
	 */
	public isEmpty(obj: Object): boolean {
		for (const i in obj) {
			if (obj.hasOwnProperty(i)) {
				return false;
			}
		}
		return true;
	}

	/**
	 * safeBtoA
	 *
	 * @param str
	 */
	public safeBtoA(str: string): string {
		return btoa(str);
	}

	/**
	 * safeAtoB
	 *
	 * @param str
	 * @param emptyReplacementValue
	 */
	public safeAtoB(str: string, emptyReplacementValue: string = ""): string {
		try {
			if (atob(btoa(str)) == str) {
				return atob(str);
			} else {
				return str || emptyReplacementValue;
			}
		} catch (ex) {
			return str || emptyReplacementValue;
		}
	}

	/**
	 * areArraysEqual
	 *
	 * @param arr1
	 * @param arr2
	 */
	public areArraysEqual(arr1: any[], arr2: any[]) {
		if (!Array.isArray(arr1) || !Array.isArray(arr2) || arr1.length !== arr2.length) {
			return false;
		} else {
			const _arr1 = arr1.concat().sort();
			const _arr2 = arr2.concat().sort();

			for (let i = 0; i < arr1.length; i++) {
				if (_arr1[i] !== _arr2[i]) {
					return false;
				}
			}

			return true;
		}
	}

	/**
	 * merge 2 Objects
	 *
	 * @param obj1
	 * @param obj2
	 */
	public mergeObjects(obj1: any, obj2: any) {
		const mergedObject = {};

		for (let attrname in obj1) {
			mergedObject[attrname] = obj1[attrname];
		}
		for (let attrname in obj2) {
			mergedObject[attrname] = obj2[attrname];
		}

		return mergedObject;
	}

	/**
	 * wait for html element by id
	 *
	 * @param htmlElementId
	 */
	public waitForElementById(htmlElementId: string): Promise<{success: boolean; element: HTMLElement}> {
		return new Promise<{success: boolean; element: HTMLElement}>((resolve, reject) => {
			let maxIntervals = 100;
			let intervalHandler = interval(100).pipe(take(100));
			intervalHandler.subscribe(
				(count) => {
					let foundElement = $(`#${htmlElementId}`);
					if (foundElement && foundElement.length > 0) {
						resolve({
							success: true,
							element: foundElement.get(0),
						});
					}
				},
				(error) => {
				},
				() => {
					resolve({success: false, element: null});
				},
			);
		});
	}

	/**
	 * jsDateToNetData converts to netDate
	 * [StackOverflow]{@link http://stackoverflow.com/questions/12385271/convert-javascript-date-to-asp-net-date-format}
	 *
	 * @param date
	 */
	public jsDateToNetDate(date: Date): string {
		const str1 = dayjs(date).valueOf();
		const str2 = dayjs(date).format("ZZ");

		return "/Date(" + str1 + str2 + ")/";
	}

	public jsDateToDefaultDate(date: Date): string {
		return dayjs(date).format("YYYY-MM-DD");
	}

	/**
	 * isMinDate
	 *
	 * @param date
	 */
	public isMinDate(date: Date): boolean {
		let isMinDate = false;

		if (date && date instanceof Date) {
			isMinDate =
				date.getFullYear() === 1 &&
				date.getMonth() === 0 &&
				date.getDate() === 1 &&
				date.getHours() === 0 &&
				date.getMinutes() === 0 &&
				date.getSeconds() === 0;
		}

		return isMinDate;
	}

	public removeAllPropertiesByName(obj: any, propertyName: string): Object {
		return this.removeAllPropertiesByNames(obj, [propertyName]);
	}

	public removeAllPropertiesByNames(obj: any, propertyNames: string[]): Object {
		if (obj instanceof Array) {
			for (const item of obj) {
				if (item) {
					this.removeAllPropertiesByNames(<any>item, propertyNames);
				}
			}
		} else if (obj instanceof Object) {
			for (const key in obj) {
				if (obj.hasOwnProperty(key) && propertyNames.indexOf(key) >= 0) {
					delete obj[key];
				} else if (obj[key] instanceof Object) {
					this.removeAllPropertiesByNames(obj[key], propertyNames);
				}
			}
		}

		return obj;
	}

	public static sortArray(array: any[], property: string) {
		array.sort((v1: any, v2: any) => {
			if (v1[property] > v2[property]) {
				return 1;
			} else if (v1[property] < v2[property]) {
				return -1;
			} else {
				return 0;
			}
		});
	}

	public static toDate(elem: string | Date | dayjs.Dayjs): Date {
		return SimUtilityService.toDayJs(elem)?.toDate();
	}

	public static toDayJs(elem: string | Date | dayjs.Dayjs) {
		if (elem == undefined) return null;
		const date = dayjs(elem);
		return date.isValid() ? date : null;
	}

	// Deep copy
	public static copy<T1, T2>(source: T1, target: T2 = null): T1 & T2 {
		if (!source && !target) {
			return null;
		} else if (!source && target) {
			return <T1 & T2>target;
		} else if (source && !target) {
			return <T1 & T2>$.extend(true, Array.isArray(source) ? [] : {}, source);
		} else if (source && target) {
			return $.extend(true, target, source);
		}
	}

	public static async delay(ms: number) {
		await new Promise<void>((resolve) =>
			setTimeout(() => {
				resolve();
			}, ms),
		);
	}

	public static dateToUTC(dateParam: Date) {
		const offset = dateParam.getTimezoneOffset() * 60000;
		return new Date(dateParam.getTime() + offset);
	}

	public static arrayEquals<T>(a: T[], b: T[]): boolean {
		if (a === b) {
			return true;
		}
		if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) {
			return false;
		}

		// fixed logic for sparce arrays
		for (let i = 0; i < a.length; i++) {
			const aHas = a.hasOwnProperty(i);
			const bHas = b.hasOwnProperty(i);

			// one has a hole, the other doesn't
			if (aHas !== bHas) {
				return false;
			}
			// compare defined values
			if (aHas && a[i] !== b[i]) {
				return false;
			}
		}
		return true;
	}

	public static remove<T>(array: T[], elem: T): boolean {
		const index = array?.indexOf(elem);
		if (index > -1) {
			array.splice(index, 1);
			return true;
		}
		return false;
	}

	public static dictionaryHasSameKeys<T>(a: {[key: string]: T}, b: {[key: string]: T}): boolean {
		if (a === b) {
			return true;
		}

		let aKeys = Enumerable.from(a ?? {})
			.select((x) => x.key)
			.orderBy((x) => x)
			.toArray();
		let bKeys = Enumerable.from(b ?? {})
			.select((x) => x.key)
			.orderBy((x) => x)
			.toArray();
		return SimUtilityService.arrayEquals(aKeys, bKeys);
	}

	/**
	 * https://developer.mozilla.org/en-US/docs/Web/API/AudioParam/value
	 * @param gainNode
	 * @param volume Value between 0 and 100
	 */
	public static setGainNodeValue(gainNode: GainNode, volume: number) {
		try {
			if (gainNode) {
				//set volume to full if not set
				volume ??= 100;
				//bound volume between 0 and 100
				volume = Math.min(Math.max(volume, 0), 100);

				volume /= 100;
				if (gainNode.gain.value !== volume) {
					gainNode.gain.value = volume;
				} else {
					//volume already set - no change needed
				}
			} else {
				console.info("GainNode not set!");
			}
		} catch (ex) {
			console.error("setGainNodeValue" + ex.toString());
		}
	}

	public static setAudioElementVolume(audioElement: HTMLAudioElement, volume: number) {
		try {
			if (audioElement) {
				//set volume to full if not set
				volume ??= 100;
				//bound volume between 0 and 100
				volume = Math.min(Math.max(volume, 0), 100);

				volume /= 100;
				if (audioElement.volume !== volume) {
					audioElement.volume = volume;
				} else {
					//volume already set - no change needed
				}
			} else {
				console.info("audioElement not set!");
			}
		} catch (ex) {
			console.error("setAudioElementVolume" + ex.toString());
		}
	}

	public static arraysAreIdentical<T>(
		arr1: T[],
		arr2: T[],
		objectParams?: {
			propertyNameToComapare: string;
			sortFunction: (a: T, b: T) => number;
		},
	): boolean {
		if (arr1 == arr2) return true;
		if (!arr1 || !arr2) return false;
		if (arr1.length !== arr2.length) return false;
		let arr1C: T[];
		let arr2C: T[];
		if (!objectParams) {
			arr1C = arr1.toSorted();
			arr2C = arr2.toSorted();
		} else {
			arr1C = arr1.toSorted(objectParams?.sortFunction);
			arr2C = arr2.toSorted(objectParams?.sortFunction);
		}
		if (objectParams?.propertyNameToComapare?.length > 0) {
			for (let i = 0, len = arr1.length; i < len; i++) {
				if (arr1C[i][objectParams?.propertyNameToComapare] !== arr2C[i][objectParams?.propertyNameToComapare]) {
					return false;
				}
			}
		} else {
			return isEqual(arr1C, arr2C);
		}

		return true;
	}

	public static getRecordingStart(recording: ScenarioRecording): Date {
		if (recording?.recordingScenes?.length) {
			return SimUtilityService.toDate(recording.recordingScenes[0].start);
		}
		return new Date();
	}

	public static getRecordingEnd(recording: ScenarioRecording): Date {
		if (recording?.recordingScenes?.length) {
			return SimUtilityService.toDate(recording.recordingScenes[recording.recordingScenes.length - 1].end);
		}
		return new Date();
	}

	public static getStartofDay(elem: string | Date | dayjs.Dayjs) {
		const date = SimUtilityService.toDayJs(elem)?.startOf("day");
		return date;
	}

	public static getEndofDay(elem: string | Date | dayjs.Dayjs) {
		const date = SimUtilityService.toDayJs(elem)?.endOf("day");
		return date;
	}

	public static getStartofMonth(elem: string | Date | dayjs.Dayjs) {
		const date = SimUtilityService.toDayJs(elem)?.startOf("month");
		return date;
	}

	public static getEndofMonth(elem: string | Date | dayjs.Dayjs) {
		const date = SimUtilityService.toDayJs(elem)?.endOf("month");
		return date;
	}

	public static isInElectron(): boolean {
		// Detect the user agent when the `nodeIntegration` option is set to false
		if (typeof navigator === "object" && typeof navigator.userAgent === "string" && navigator.userAgent.indexOf("Electron") >= 0) {
			return true;
		}

		return false;
	}

	public static getFormattedSchedulingConflictMessage(conflict: SchedulingConflict): string {
		const startDate = dayjs(conflict.conflictStart).format("YYYY-MM-DD HH:mm A");
		const endDate = dayjs(conflict.conflictEnd).format("YYYY-MM-DD HH:mm A");

		let dateString = startDate + " - " + endDate + " : " + conflict.resourceName;
		if (conflict.conflictName) {
			dateString += " (" + conflict.conflictName + ")";
		}
		return dateString;
	}

	public static getStringValuesOfEnum(enumType): string[] {
		let allKeys = Object.values(enumType);
		const stringKeys = allKeys.filter((value: string | number) => {
			return typeof value === "string";
		});
		return stringKeys.map((key) => `${key}`);
	}

	public static getValuesOfEnum(enumType): number[] {
		let allKeys = Object.values(enumType);
		const stringKeys = allKeys.filter((value: string | number) => {
			return !isNaN(Number(value));
		});
		return stringKeys.map((key) => Number(key));
	}

	public static async promiseTakeAtLeast<A>(ms: number, promise: Promise<A>) {
		let ret = await Promise.all([SimUtilityService.delay(ms), promise]);
		return ret[1];
	}

	public static roomHasAnyNonEmptyLayout(trainingRoom: TrainingRoom) {
		return Enumerable.from(trainingRoom?.roomLayout)
			?.selectMany((x) => x?.assignedCameraIds)
			?.any((x) => !!x && x !== SimUtilityService.emptyGuid());
	}

	public static sanitizeHtmlPlayerTimes(val: number) {
		if (isNaN(val)) return 0;
		return val;
	}

	public static progressFromDisplayedMedia(currentMedia: DisplayedMedia) {
		if (currentMedia?.totalLength) {
			return currentMedia?.currentPosition / currentMedia?.totalLength;
		}
		return 0;
	}

	public static formatBytes(bytes: number, decimals: number = 2) {
		if (!+bytes) return "0 Bytes";

		const k = 1024;
		const dm = decimals < 0 ? 0 : decimals;
		const sizes = ["Bytes", "KB", "MB", "GB", "TB", "PB", "EB", "ZB", "YB"];

		const i = Math.floor(Math.log(bytes) / Math.log(k));

		return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
	}

	static getEarliestScenarioForSession(session: ISimSession) {
		if (TypePredicates.isNonSimulationSession(session)) {
			return session.startTime;
		} else if (TypePredicates.isTeamTrainingSession(session)) {
			return session.scenarioRecordingGroups
				.filter((scg) => scg?.plannedStart !== null && scg?.plannedStart !== undefined)
				.sort((scgA, scgB) => {
					return new Date(scgA?.plannedStart).getTime() - new Date(scgB?.plannedStart).getTime();
				})[0]?.plannedStart;
		}
	}

	static getOrderFromSession(session: ISimSession) {
		switch (session.type) {
			case SessionType.NonSimulation:
				return session.startTime
					? parseInt(new Date(session.startTime).getHours() + "" + new Date(session.startTime).getMinutes())
					: 0;
			case SessionType.TeamTrainingSession:
				let earliestRun = SimUtilityService.getEarliestScenarioForSession(session);
				return earliestRun ? parseInt(new Date(earliestRun).getHours() + "" + new Date(earliestRun).getMinutes()) : 0;
			case SessionType.Exam:
				return session.startTime
					? parseInt(new Date(session.startTime).getHours() + "" + new Date(session.startTime).getMinutes())
					: 0;
		}
	}

	public static isAnyClient(clientProperties: ClientProperties, webClientInfoId: string) {
		return (
			clientProperties?.recorder?.id === webClientInfoId ||
			clientProperties?.tablet?.id === webClientInfoId ||
			clientProperties?.trainingRoomPc?.id === webClientInfoId ||
			clientProperties?.viewingStation?.id === webClientInfoId ||
			clientProperties?.player?.id === webClientInfoId
		);
	}

	public static calculateAbsoluteTime(start: string | Date, position: number): Date {
		let startTime: Date;
		if (start instanceof Date) {
			startTime = start;
		} else {
			try {
				if (!!start) {
					startTime = new Date(start);
				}
			} catch (e) {
				console.error("Error parsing date, using current date instead", start);
			}
			if (!startTime) {
				startTime = new Date();
			}
		}
		position = typeof position === "number" ? position : 0;
		return new Date(startTime.getTime() + position);
	}

	public static stripHtml(html: string) {
		return html.replace(/<[^>]*>/g, "");
	}

	/**
	 * Converts LoadingDataParams to SearchParameters. To be deleted once LoadingDataParams are purged from the codebase.
	 * See SIMSOFT-8953
	 */
	public static convertLoadingParamsToSearchParams(loadingDataParams: LoadingDataParams): SearchParameters {
		return {
			searchFilters: [
				{
					searchTerms: [loadingDataParams?.searchTerm],
					searchColumn: loadingDataParams?.searchColumn,
				},
			],
			pageNumber: loadingDataParams?.pageIndex ?? 10000,
			sortAscending: loadingDataParams?.sortDirection === "asc",
			pageSize: loadingDataParams?.pageSize ?? 0,
			sortColumn: loadingDataParams?.sortActiveColumn,
		} as SearchParameters;
	}

	public static unsorted<K, V>(a: KeyValue<K, V>, b: KeyValue<K, V>) {
		return 0;
	}

	public static getScenarioRecordingProgressText(scenarioRecording: IIsReadyForArchive) {
		if (scenarioRecording.isReadyForArchive) {
			return Translations.BUTTON_PLAY;
		} else {
			const progress = Math.min(100, Math.max(0, scenarioRecording.postProcessingProgress || 0));
			return `Progress: ${progress.toFixed(0)}%`;
		}
	}

	/**
	 * Returns the timezone offset in the format of "+HH:MM" or "-HH:MM" (e.g. +02:00)
	 */
	public static getTimezoneOffsetString() {
		let offset = new Date().getTimezoneOffset();
		const sign = offset < 0 ? "+" : "-";
		offset = Math.abs(offset);
		const hours = Math.floor(offset / 60);
		const minutes = offset % 60;
		const paddedHours = hours.toString().padStart(2, "0");
		const paddedMinutes = minutes.toString().padStart(2, "0");
		return `${sign}${paddedHours}:${paddedMinutes}`;
	}

	public static ParticipantType_FontAwesomeCode_Mapping = {
		[ParticipantType.Instructor]: {fontSet: "fad", fontIcon: "fa-user-md"},
		[ParticipantType.StandardizedPatient]: {fontSet: "fad", fontIcon: "fa-user-injured"},
		[ParticipantType.Learner]: {fontSet: "fad", fontIcon: "fa-user-graduate"},
	} as const;

	public static mapParticipantToParticipantDisplay(participant: Participant) {
		return {
			...participant,
			fontIcon:
				this.ParticipantType_FontAwesomeCode_Mapping[
					participant.participantType as keyof typeof this.ParticipantType_FontAwesomeCode_Mapping
					],
			fullName: participant?.user?.fullname,
		};
	}
}
